/**
 * YouTube 추천 메타데이터 커스텀 훅
 *
 * 추천 목록 조회, AI 생성, 선택 로직을 캡슐화합니다.
 */

import { useState, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { LanguageCode } from '@/components/playlists/detail/LanguageSelector';
import { getPlaylistRetrieveQueryKey } from '@/lib/api/orval/endpoints/playlists/playlists';
import {
  getPlaylistYoutubeSuggestionsListQueryKey,
  usePlaylistSelectYoutubeSuggestion,
  usePlaylistYoutubeSuggestionsList,
  usePlaylistYoutubeSuggestionsList2,
} from '@/lib/api/orval/endpoints/youtube-suggestions/youtube-suggestions';
import type { YouTubeMetadataSuggestion } from '@/lib/api/orval/models';

interface UseYouTubeSuggestionsOptions {
  playlistId: string;
  isCompleted: boolean;
}

export function useYouTubeSuggestions({ playlistId, isCompleted }: UseYouTubeSuggestionsOptions) {
  const queryClient = useQueryClient();
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>('ko');

  // 추천 목록 조회
  const { data: suggestionsData, isLoading: isLoadingSuggestions } =
    usePlaylistYoutubeSuggestionsList(playlistId, undefined, {
      query: {
        enabled: isCompleted,
      },
    });

  // 추천 생성 mutation
  const generateMutation = usePlaylistYoutubeSuggestionsList2({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getPlaylistYoutubeSuggestionsListQueryKey(playlistId),
        });
      },
    },
  });

  // 추천 선택 mutation
  const selectMutation = usePlaylistSelectYoutubeSuggestion({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getPlaylistYoutubeSuggestionsListQueryKey(playlistId),
        });
        queryClient.invalidateQueries({
          queryKey: getPlaylistRetrieveQueryKey(playlistId),
        });
      },
    },
  });

  // 선택된 언어로 필터링된 추천
  const filteredSuggestions = useMemo(() => {
    if (!suggestionsData?.results) {
      return [];
    }
    return suggestionsData.results.filter(
      (s: YouTubeMetadataSuggestion) => s.language === selectedLanguage
    );
  }, [suggestionsData?.results, selectedLanguage]);

  // 배치별 그룹화
  const suggestionsByBatch = useMemo(() => {
    const grouped: Record<number, YouTubeMetadataSuggestion[]> = {};
    filteredSuggestions.forEach((s: YouTubeMetadataSuggestion) => {
      const batch = s.generationBatch || 1;
      if (!grouped[batch]) {
        grouped[batch] = [];
      }
      grouped[batch].push(s);
    });
    return Object.entries(grouped)
      .sort(([a], [b]) => Number(b) - Number(a))
      .map(([batch, suggestions]) => ({
        batch: Number(batch),
        suggestions: suggestions.sort(
          (a, b) => (a.suggestionOrder || 0) - (b.suggestionOrder || 0)
        ),
      }));
  }, [filteredSuggestions]);

  const hasAnySuggestions = !!(suggestionsData?.count && suggestionsData.count > 0);

  const handleGenerateSuggestions = () => {
    generateMutation.mutate({
      id: playlistId,
      data: {},
    });
  };

  const handleSelectSuggestion = (suggestionId: string) => {
    selectMutation.mutate({
      id: playlistId,
      data: { suggestionId },
    });
  };

  return {
    // 상태
    selectedLanguage,
    setSelectedLanguage,
    isLoadingSuggestions,
    isGenerating: generateMutation.isPending,

    // 데이터
    hasAnySuggestions,
    suggestionsByBatch,

    // 선택 관련
    selectMutation,

    // 핸들러
    handleGenerateSuggestions,
    handleSelectSuggestion,
  };
}
